import React from 'react';
import { DEFAULT_THEME, THEME_PRESETS } from '../constants/theme';
import { applyClubTheme } from '../utils/theme';
import ClubLogoSet from './ClubLogoSet';

function ClubThemeSettings({ API, club, clubEditForm, clubs, currentUser, setClubEditForm, setWebsiteThemeSuggestion, showToast, siteTheme, websiteThemeSuggestion }) {
  return (
    <div style={{ gridColumn: '1 / -1' }}>
    {/* One logo became three, each named, one of them the default. The
        upload here used to read the club's brand colours off the image as
        well; that job is still done by "Load colours from website" below,
        from a source a club is more likely to have than a tidy logo. */}
    <ClubLogoSet club={club} clubEditForm={clubEditForm} setClubEditForm={setClubEditForm} showToast={showToast} />

    <label className="w3-tiny w3-text-grey"><i className="fa fa-paint-brush w3-margin-right"></i>Club Theme Colours (60 / 30 / 10 rule)</label>
    {/* Use site default toggle */}
    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '8px 0 6px', cursor: 'pointer' }}>
      <input type="checkbox"
        checked={!!clubEditForm.useDefaultTheme}
        onChange={e => {
          const checked = e.target.checked;
          setClubEditForm(f => ({ ...f, useDefaultTheme: checked }));
          if (checked) {
            // revert preview to site theme
            const st = siteTheme || {};
            applyClubTheme(st.c60, st.c30, st.c10);
          } else {
            applyClubTheme(clubEditForm.themeC60, clubEditForm.themeC30, clubEditForm.themeC10);
          }
        }}
        style={{ width: 16, height: 16 }}
      />
      <span className="w3-small">Use site default theme</span>
    </label>
    {/* Quick-pick presets — only shown when custom is selected */}
    {!clubEditForm.useDefaultTheme && (<>
    {/* Load from website button */}
    {clubEditForm.website && (
      <button type="button" className="w3-button w3-tiny w3-round w3-theme-l3 w3-margin-bottom"
        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
        onClick={async e => {
          const btn = e.currentTarget;
          btn.disabled = true;
          btn.innerHTML = '<i class="fa fa-spinner fa-spin w3-margin-right"></i>Fetching…';
          try {
            const r = await fetch(`${API}/api/auth/clubs/suggest-theme`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ url: clubEditForm.website }),
            });
            if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.error || r.status); }
            const suggestion = await r.json();
            setWebsiteThemeSuggestion(suggestion);
            try { localStorage.setItem(`gs_web_theme_${club.id}`, JSON.stringify(suggestion)); } catch {}
            if (suggestion.warning) {
              showToast(suggestion.warning, 'orange');
            } else {
              showToast('Club website colours ready — click the "Club Website" swatch to apply');
            }
          } catch (err) {
            showToast(`Could not fetch website: ${err.message}`, 'red');
          } finally {
            btn.disabled = false;
            btn.innerHTML = '<i class="fa fa-globe w3-margin-right"></i>Load colours from website';
          }
        }}>
        <i className="fa fa-globe w3-margin-right"></i>Load colours from website
      </button>
    )}
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '8px 0 10px' }}>
      {websiteThemeSuggestion && (
        <button type="button" title={websiteThemeSuggestion.warning || 'Colours extracted from the club\'s own website logo'}
          onClick={() => {
            setClubEditForm(f => ({ ...f, themeC60: websiteThemeSuggestion.c60, themeC30: websiteThemeSuggestion.c30, themeC10: websiteThemeSuggestion.c10 }));
            applyClubTheme(websiteThemeSuggestion.c60, websiteThemeSuggestion.c30, websiteThemeSuggestion.c10);
          }}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
            padding: '6px 8px', border: `2px solid ${websiteThemeSuggestion.warning ? '#e65100' : '#2f6f63'}`, borderRadius: '8px',
            background: '#fff', cursor: 'pointer', minWidth: '64px',
          }}>
          {websiteThemeSuggestion.logoUrl
            ? <img src={websiteThemeSuggestion.logoUrl} alt="Club logo" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4 }} />
            : <div style={{ display: 'flex', gap: '3px' }}>
                <span style={{ width: 13, height: 13, borderRadius: '50%', background: websiteThemeSuggestion.c60, border: '1px solid #bbb', display: 'inline-block' }} />
                <span style={{ width: 13, height: 13, borderRadius: '50%', background: websiteThemeSuggestion.c30, border: '1px solid rgba(0,0,0,.08)', display: 'inline-block' }} />
                <span style={{ width: 13, height: 13, borderRadius: '50%', background: websiteThemeSuggestion.c10, border: '1px solid rgba(0,0,0,.08)', display: 'inline-block' }} />
              </div>
          }
          <span style={{ fontSize: 'var(--gs-text-xs)', fontWeight: 700, color: websiteThemeSuggestion.warning ? '#e65100' : '#2f6f63', whiteSpace: 'nowrap' }}>
            {websiteThemeSuggestion.warning ? '⚠ Club Website' : 'Club Website'}
          </span>
        </button>
      )}
      {THEME_PRESETS.map(p => (
        <button key={p.name} type="button" title={p.name}
          onClick={() => {
            setClubEditForm(f => ({ ...f, themeC60: p.c60, themeC30: p.c30, themeC10: p.c10 }));
            applyClubTheme(p.c60, p.c30, p.c10);
          }}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
            padding: '6px 8px', border: '1px solid #ccc', borderRadius: '8px',
            background: '#fff', cursor: 'pointer', minWidth: '64px',
          }}>
          <div style={{ display: 'flex', gap: '3px' }}>
            <span style={{ width: 13, height: 13, borderRadius: '50%', background: p.c60, border: '1px solid #bbb', display: 'inline-block' }} />
            <span style={{ width: 13, height: 13, borderRadius: '50%', background: p.c30, border: '1px solid rgba(0,0,0,.08)', display: 'inline-block' }} />
            <span style={{ width: 13, height: 13, borderRadius: '50%', background: p.c10, border: '1px solid rgba(0,0,0,.08)', display: 'inline-block' }} />
          </div>
          <span style={{ fontSize: 'var(--gs-text-xs)', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>{p.name}</span>
        </button>
      ))}
    </div>
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '6px', alignItems: 'flex-start' }}>
      {[
        { key: 'themeC60', label: '60% – Background', desc: 'Page bg & card surfaces (light/neutral)' },
        { key: 'themeC30', label: '30% – Navbar & Headers', desc: 'Main club colour' },
        { key: 'themeC10', label: '10% – Accent', desc: 'Buttons & active elements' },
      ].map(({ key, label, desc }) => (
        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="color"
            value={clubEditForm[key] || DEFAULT_THEME[key.replace('themeC', 'c').replace('60','60').replace('30','30').replace('10','10')]}
            onChange={e => {
              const val = e.target.value;
              setClubEditForm(f => ({ ...f, [key]: val }));
              if (club.id === currentUser?.ClubId) applyClubTheme(
                key === 'themeC60' ? val : (clubEditForm.themeC60 || DEFAULT_THEME.c60),
                key === 'themeC30' ? val : (clubEditForm.themeC30 || DEFAULT_THEME.c30),
                key === 'themeC10' ? val : (clubEditForm.themeC10 || DEFAULT_THEME.c10),
              );
            }}
            style={{ width: '44px', height: '36px', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer', padding: '2px' }}
            title={desc}
          />
          <div>
            <div className="w3-tiny w3-text-grey" style={{ fontWeight: 700 }}>{label}</div>
            <div className="w3-tiny w3-text-grey">{desc}</div>
          </div>
        </div>
      ))}
    </div>
    </>) }
    </div>
  );
}

export default ClubThemeSettings;
